import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddressSearch from '../AddressSearch';
import { categories } from './constants';
import { useTranslation } from '../../hooks/useTranslation';

const HeroSection = ({
  selectedCategory,
  setSelectedCategory,
  selectedLocation,
  setSelectedLocation
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  // Images de background des barbiers et salons de barbier
  const backgroundImages = [
    '/images/agustin-fernandez-1Pmp9uxK8X8-unsplash.jpg',
    '/images/hai-phung-m4Pd_e-4zKs-unsplash.jpg',
    '/images/nate-johnston-tgPrIYnW3g4-unsplash.jpg',
    '/images/nathon-oski-EW_rqoSdDes-unsplash.jpg',
    '/images/nathon-oski-fE42nRlBcG8-unsplash.jpg'
  ];

  const [currentBgIndex, setCurrentBgIndex] = useState(0);

  // Rotation automatique des backgrounds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % backgroundImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [backgroundImages.length]);

  return (
    <Box
      sx={{
        position: 'relative',
        pt: { xs: 3, md: 10 },
        pb: { xs: 3, md: 3 },
        textAlign: 'center',
        overflow: 'hidden',
        minHeight: { xs: 'auto', md: '70vh' },
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {/* Backgrounds animés */}
      {backgroundImages.map((image, index) => (
        <Box
          key={index}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: currentBgIndex === index ? 0.6 : 0,
            transition: 'opacity 2s ease-in-out',
            zIndex: 0
          }}
        />
      ))}

      {/* Overlay gradient */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(250, 250, 250, 0.09) 0%, rgba(245, 245, 245, 0.15) 100%)',
          zIndex: 1
        }}
      />

      {/* Overlay décoratif */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(245, 245, 220, 0.2) 0%, transparent 50%)',
          zIndex: 2,
          pointerEvents: 'none'
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Title */}
        <Box sx={{ mb: { xs: 2, md: 8 } }}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontWeight: 800,
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.8rem', lg: '4.2rem' },
              color: '#212121',
              fontFamily: '"Inter", sans-serif',
              lineHeight: { xs: 1.2, md: 1.1 },
              letterSpacing: '-0.02em',
              background: 'linear-gradient(135deg, #212121 0%, #424242 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            {t('hero.title')}
            <br />
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(45deg, #000000 0%, #666666 50%, #000000 100%)',
                backgroundSize: '200% 100%',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'gradientShift 3s ease-in-out infinite',
                '@keyframes gradientShift': {
                  '0%': { backgroundPosition: '0% 50%' },
                  '50%': { backgroundPosition: '100% 50%' },
                  '100%': { backgroundPosition: '0% 50%' }
                }
              }}
            >
              {t('hero.titleHighlight')}
            </Box>
          </Typography>
          
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              mb: { xs: 2, md: 4 },
              fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
              fontWeight: 400,
              maxWidth: { xs: '100%', md: 650 },
              mx: 'auto',
              fontFamily: '"Inter", sans-serif',
              lineHeight: 1.6,
              color: '#666'
            }}
          >
            {t('hero.subtitle')}
          </Typography>
        </Box>

        
        {/* Enhanced Search Bar - Hidden on mobile */}
        <Paper
          elevation={0}
          sx={{
            display: { xs: 'none', md: 'block' },
            p: { sm: 3, md: 3.5 },
            mb: { md: 4 },
            border: '1px solid rgba(0,0,0,0.08)',
            backgroundColor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
            maxWidth: { md: 1000 },
            mx: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.08), 0 8px 25px rgba(0,0,0,0.06)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 25px 70px rgba(0,0,0,0.12), 0 10px 30px rgba(0,0,0,0.08)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-around',
              alignItems: 'stretch',
              gap: 3
            }}
          >
            {/* Category Selection */}
            <Box sx={{ flex: '0 0 30%' }}>
              <Box sx={{ textAlign: 'left', mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem'
                  }}
                >
                  {t('hero.serviceLabel')}
                </Typography>
              </Box>
              <FormControl fullWidth size="medium">
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: 3,
                    backgroundColor: '#f8f9fa',
                    border: 'none',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '& .MuiSelect-select': {
                      py: 2,
                      fontSize: '1rem',
                      fontWeight: 500
                    },
                    '&:hover': {
                      backgroundColor: '#f0f1f2'
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      boxShadow: '0 0 0 2px rgba(245, 245, 220, 0.3)'
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    <Box display="flex" alignItems="center" gap={1} sx={{ color: '#999' }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          backgroundColor: '#ddd',
                          borderRadius: '50%'
                        }}
                      />
                      {t('hero.chooseService')}
                    </Box>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category} sx={{ py: 1.5 }}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            {/* Location Search */}
            <Box sx={{ flex: '0 0 40%' }}>
              <Box sx={{ textAlign: 'left', mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem'
                  }}
                >
                  {t('hero.locationLabel')}
                </Typography>
              </Box>
              <Box sx={{
                '& .MuiTextField-root': {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: '#f8f9fa',
                    border: 'none',
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover': {
                      backgroundColor: '#f0f1f2'
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                      boxShadow: '0 0 0 2px rgba(245, 245, 220, 0.3)'
                    },
                    '& input': {
                      py: 2,
                      fontSize: '1rem',
                      fontWeight: 500
                    }
                  }
                }
              }}>
                <AddressSearch
                  value={selectedLocation ? selectedLocation.query : ''}
                  onChange={setSelectedLocation}
                  placeholder={t('hero.locationPlaceholder')}
                  theme="light"
                  sx={{
                    '& .MuiPaper-root': {
                      zIndex: 2000, // Ensure dropdown appears above other elements
                    }
                  }}
                />
              </Box>
            </Box>
            
            {/* Search Button */}
            <Box sx={{ flex: '0 0 25%' }}>
              <Box sx={{ textAlign: 'left', mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'transparent',
                    fontSize: '0.75rem'
                  }}
                >
                  {t('hero.actionLabel')}
                </Typography>
              </Box>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => {
                  const featuredSection = document.getElementById('featured-salons');
                  if (featuredSection) {
                    featuredSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                sx={{
                  py: 1.5,
                  backgroundColor: 'transparent',
                  color: '#666',
                  borderColor: '#e0e0e0',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                    borderColor: '#ccc',
                    color: '#555'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {t('hero.searchButton')}
              </Button>
            </Box>
          </Box>
          
          {/* Add establishment link - Desktop only */}
          <Box
            textAlign="center"
            sx={{
              pt: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={() => navigate('/auth?mode=signup')}
              sx={{
                backgroundColor: '#212121',
                color: 'white',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                px: 5,
                py: 2,
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(33, 33, 33, 0.15)',
                '&:hover': {
                  backgroundColor: '#424242',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(33, 33, 33, 0.2)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('hero.addEstablishment')}
            </Button>
          </Box>
        </Paper>
        
        {/* Enhanced Mobile-First CTA Section */}
        <Box sx={{ mt: { xs: 3, md: 4 } }}>
          {isMobile && (
            <Box
              sx={{
                mb: 4,
                p: 3,
                background: 'linear-gradient(135deg, rgba(245, 245, 220, 0.1) 0%, rgba(255, 255, 255, 0.8) 100%)',
                borderRadius: 4,
                border: '1px solid rgba(245, 245, 220, 0.2)',
                textAlign: 'center'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: '#555',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  lineHeight: 1.4,
                  background: 'linear-gradient(135deg, #000000 0%, #666666 50%, #000000 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {t('hero.readyToStart')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666666',
                  fontSize: '0.95rem',
                  fontWeight: 400,
                  lineHeight: 1.5
                }}
              >
                {t('hero.joinThousands')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Enhanced trust indicators with animated text */}
        <Box
          sx={{
            mt: { xs: 2, md: 5 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 3, sm: 4 },
            p: { xs: 4, md: 4 },
            background: 'linear-gradient(135deg, rgba(245, 245, 220, 0.15) 0%, rgba(255, 215, 0, 0.08) 50%, rgba(245, 245, 220, 0.15) 100%)',
            borderRadius: { xs: 4, md: 3 },
            border: '1px solid rgba(245, 245, 220, 0.3)',
            boxShadow: '0 4px 20px rgba(245, 245, 220, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
              animation: 'shimmer 3s infinite',
              '@keyframes shimmer': {
                '0%': { left: '-100%' },
                '100%': { left: '100%' }
              }
            }
          }}
        >
          {[
            'Inscription gratuite',
            'Réservation instantanée',
            'Satisfaction garantie'
          ].map((text, index) => (
            <Box
              key={text}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                opacity: 1,
                animation: `fadeInUp 0.8s ease-out ${index * 0.2}s both`,
                '@keyframes fadeInUp': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(20px)'
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)'
                  }
                }
              }}
            >
              <Box
                sx={{
                  width: { xs: 10, md: 8 },
                  height: { xs: 10, md: 8 },
                  background: 'linear-gradient(45deg, #f5f5dc 0%, #ffd700 50%, #f5f5dc 100%)',
                  borderRadius: '50%',
                  flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(255, 215, 0, 0.3)',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(1)', opacity: 1 },
                    '50%': { transform: 'scale(1.1)', opacity: 0.8 },
                    '100%': { transform: 'scale(1)', opacity: 1 }
                  }
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: '#000000',
                  fontSize: { xs: '1rem', md: '0.95rem' },
                  fontWeight: 600,
                  letterSpacing: '0.3px',
                  background: 'linear-gradient(135deg, #000000 0%, #666666 30%, #333333 50%, #666666 70%, #000000 100%)',
                  backgroundSize: '200% 100%',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: 'textShine 3s ease-in-out infinite',
                  '@keyframes textShine': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' }
                  }
                }}
              >
                {text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;